import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';

import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import Logo from '@portal/assets/svg/logos/login_logo.svg?react';
import { onboarding, passwordRules } from '@portal/config/app.json';
import { LOGO_LABEL } from '@portal/config/env';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import {
  AccountStatus,
  AccountTaxIdentifierType,
} from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { maskCpf, maskCpfOrCnpj, unmaskField } from '@portal/services/masks';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import Loading from '@portal/components/Loading/Loading';
import { getRouteStackPath } from '@portal/config/routes';
import { useAppIntegrityRequired } from '@portal/hooks/useAppIntegrityRequired';
import useQuery from '@portal/hooks/useQuery';
import useReduxState from '@portal/hooks/useReduxState';
import NavigationService from '@portal/services/navigation';
import * as detectIncognito from 'detect-incognito';

import { LoadingActions, SettingsActions } from '@portal/actions';
import { WARN_LOGIN_ATTEMPTS_ERROR } from '@portal/actions/actionTypes';
import * as AuthActions from '@portal/actions/auth';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import * as MessageService from '@portal/services/message';

import loginBackground from '../../../assets/img/background.png';
import { useNavigation } from '@portal/hooks/useNavigation';
import RecaptchaCheckbox from '@portal/components/RecaptchaCheckbox/RecaptchaCheckbox';
import './Login.less';

const initialValues: models.AuthRequest = {
  password: '',
  username: '',
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    user: {
      me: { mustChangePassword, mustChangePin, account, login },
      pendingDocuments,
      pendingDocumentsError,
    },
    loading: { amount: isLoading },
    settings: { remoteConfig },
    auth: { credentialsError, showRecoveryPassowordWarnModal },
    theme: { currentTheme },
  } = useReduxState();
  const query = useQuery();
  const recaptchaRequired = useAppIntegrityRequired();
  const ssoToken = query.get('sso-token');
  const accessToken = query.get('access-token');
  const refreshToken = query.get('refresh-token');

  const [showPassword, setShowPassword] = useState(false);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const [form, setForm] = useState(initialValues);

  const navigateToPendingDocuments = useCallback(() => {
    if (pendingDocuments && pendingDocuments.length > 0) {
      navigate(getRouteStackPath('AUTH', 'PENDING_DOCUMENTS'));
      return true;
    }
    return false;
  }, [navigate, pendingDocuments]);

  const navigateToChangeCredentials = useCallback(() => {
    if (mustChangePassword) {
      navigate(getRouteStackPath('AUTH', 'CHANGE_PASSWORD'));
      return true;
    }
    if (mustChangePin) {
      navigate(getRouteStackPath('AUTH', 'CHANGE_PIN'));
      return true;
    }
    return false;
  }, [navigate, mustChangePassword, mustChangePin]);

  const navigateBasedOnAccountStatus = useCallback(() => {
    if (!account.status) {
      return false;
    }

    const typeAccount =
      account.type === AccountTaxIdentifierType.PERSON
        ? TaxIdentifierType.PF
        : TaxIdentifierType.PJ;

    if (account.status === AccountStatus.PENDING_ACCOUNT_DATA_RETRY) {
      navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
        state: {
          isRetry: true,
          accountType: typeAccount,
        },
      });
      return true;
    }

    if (account.status === AccountStatus.PENDING_KYC_ANALYSIS) {
      navigate(getRouteStackPath('AUTH', 'CELCOIN_DOCUMENTS'));
      return true;
    }

    if (account.status === AccountStatus.PENDING_SOCIAL_CONTRACT) {
      navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
        state: {
          isRetry: false,
          accountType: typeAccount,
        },
      });
      return true;
    }

    const newTab = NavigationService.getOnboardingTab(account.status);
    if (newTab) {
      navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
        state: {
          tab: newTab,
          accountType: typeAccount,
        },
      });
      return true;
    }

    return false;
  }, [navigate, account.status, account.type]);

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  useEffect(() => {
    if (pendingDocumentsError) {
      MessageService.error(pendingDocumentsError.message, {
        originalError: pendingDocumentsError,
      });
    }
  }, [pendingDocumentsError]);

  useEffect(() => {
    if (navigateToChangeCredentials()) {
      return;
    }

    navigateBasedOnAccountStatus();

    if (navigateToPendingDocuments()) {
      return;
    }

    navigateBasedOnAccountStatus();
  }, [
    navigateToChangeCredentials,
    navigateToPendingDocuments,
    navigateBasedOnAccountStatus,
  ]);

  const onFormChange = (key: string, value: string) => {
    setForm((prevState: models.AuthRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const detectPrivateMode = useCallback(async (): Promise<boolean> => {
    dispatch(LoadingActions.increaseLoading());

    try {
      const { isPrivate } = await detectIncognito.detectIncognito();
      return isPrivate;
    } catch {
      return false;
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  }, [dispatch]);

  const checkIncognito = useCallback(async (): Promise<boolean> => {
    const isPrivate = await detectPrivateMode();

    if (isPrivate) {
      navigate(getRouteStackPath('AUTH', 'DENIED_ACCESS'));
      return true;
    }

    return false;
  }, [navigate, detectPrivateMode]);

  useEffect(() => {
    checkIncognito();
  }, [checkIncognito]);

  const onFormSubmit = () => {
    if (recaptchaRequired && !recaptchaToken) {
      MessageService.error('RECAPTCHA_REQUIRED');

      return;
    }

    const username =
      onboarding.cpfLogin && !/^[a-zA-Z]/.test(form.username)
        ? unmaskField(form.username)
        : form.username;

    dispatch(
      AuthActions.authenticate({
        ...form,
        username,
        ...(recaptchaToken ? { recaptchaToken } : {}),
        credentialsError: credentialsError + 1,
      })
    );
  };

  useEffect(() => {
    if (ssoToken) {
      dispatch(AuthActions.validateSsoToken(ssoToken));
    }

    if (accessToken && refreshToken && !ssoToken) {
      dispatch(AuthActions.checkIsLogged(accessToken, refreshToken));
    }
  }, [dispatch, accessToken, refreshToken, ssoToken]);

  const createAccount = async () => {
    if (remoteConfig?.disableOnboard) {
      setOpenMaintenanceModal(true);
      return;
    }

    const accountType = TaxIdentifierType.PJ;

    if (!onboarding.allowOnlyCompanyAccount) {
      navigate(getRouteStackPath('AUTH', 'REGISTER'));
    } else {
      navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
        state: { accountType },
      });
    }
  };

  return (
    <div className="login" style={{ background: currentTheme.background }}>
      {isLoading > 0 && <Loading testId="login_Loading" />}
      <Helmet>
        <title>{translate('PAGES.AUTH.LOGIN.PAGE_TITLE')}</title>
      </Helmet>
      <div className="login__page">
        <div
          className="login__page__logo"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${loginBackground})`,
          }}
        />
        <div className="login__page__form">
          <h1 className="login__sr-only">
            {translate('PAGES.AUTH.LOGIN.PAGE_TITLE')}
          </h1>
          <div
            {...{ testId: 'login_Logo' }}
            className="login__container__form__inner__logo"
            aria-hidden="true"
          >
            <Logo
              className="login__container__form__inner__logo__svg"
              height={65}
            />
          </div>
          {LOGO_LABEL ? (
            <a
              className="login__container__form__inner__message__label"
              href="https://mblabs.com.br/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate('SHARED.POWERED_BY')}
            </a>
          ) : null}
          <AdvancedForm onSubmit={onFormSubmit}>
            <div className="login__page__form__inputs">
              <AdvancedInput
                testId="login_AdvancedInput_username"
                placeholder={
                  onboarding.cpfLogin
                    ? '000.000.000-00'
                    : translate(
                        `PAGES.AUTH.LOGIN.FORM.${
                          onboarding.cpfLogin ? 'TAX_IDENTIFIER' : 'USER'
                        }.LABEL`
                      ) + '\u2026'
                }
                label={translate(
                  `PAGES.AUTH.LOGIN.FORM.${
                    onboarding.cpfLogin ? 'TAX_IDENTIFIER' : 'USER'
                  }.LABEL`
                )}
                value={form.username}
                autoComplete="username"
                spellCheck={false}
                onChange={(value: string) => {
                  if (onboarding.cpfLogin && !/^[a-zA-Z]/.test(value)) {
                    if (unmaskField(value).length <= 11) {
                      onFormChange('username', maskCpf(value));
                    } else {
                      onFormChange('username', maskCpfOrCnpj(value));
                    }
                  } else {
                    onFormChange('username', value);
                  }
                }}
              />
              <AdvancedInput
                testId="login_AdvancedInput_password"
                label={translate('PAGES.AUTH.LOGIN.FORM.PASSWORD.LABEL')}
                value={form.password}
                onChange={(value: string) => onFormChange('password', value)}
                securedTextField={!showPassword}
                autoComplete="current-password"
                spellCheck={false}
                placeholder={
                  translate('PAGES.AUTH.LOGIN.FORM.PASSWORD.LABEL') + '\u2026'
                }
                endIcon={
                  <button
                    type="button"
                    className="login__password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={
                      showPassword
                        ? translate('PAGES.AUTH.LOGIN.FORM.PASSWORD.HIDE')
                        : translate('PAGES.AUTH.LOGIN.FORM.PASSWORD.SHOW')
                    }
                  >
                    {!showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </button>
                }
                maxLength={passwordRules.maxLength}
                hideMaxLength
              />
              {!!recaptchaRequired && (
                <div className="login__page__form__recaptcha">
                  <RecaptchaCheckbox
                    onVerify={setRecaptchaToken}
                    onExpire={() => setRecaptchaToken(null)}
                  />
                </div>
              )}
              <div className="login__page__form__actions">
                <AdvancedButton
                  testId="login_Button_Submit"
                  text={translate('PAGES.AUTH.LOGIN.FORM.BUTTON.TEXT')}
                  fullWidth
                  type="submit"
                />
              </div>
              <div className="login__bottom-section">
                <div className="login__forgot-links">
                  <button
                    type="button"
                    className="login__forgot-btn"
                    onClick={() =>
                      navigate(getRouteStackPath('AUTH', 'RECOVER_PASSWORD'), {
                        state: {
                          userLogin: login,
                        },
                      })
                    }
                  >
                    {translate('PAGES.AUTH.LOGIN.FORM.FORGOT_PASSWORD.TEXT')}
                  </button>
                  {!onboarding.cpfLogin && (
                    <button
                      type="button"
                      className="login__forgot-btn login__forgot-btn--primary"
                      onClick={() =>
                        navigate(getRouteStackPath('AUTH', 'RECOVER_LOGIN'), {
                          state: {
                            login: true,
                          },
                        })
                      }
                    >
                      {translate('PAGES.AUTH.LOGIN.FORM.FORGOT_LOGIN.TEXT')}
                    </button>
                  )}
                </div>

                {!!onboarding.enabled && (
                  <>
                    <div className="login__divider-row">
                      <span className="login__divider-line" />
                      <span className="login__divider-text">
                        {translate('PAGES.AUTH.LOGIN.FORM.CREATE_ACCOUNT.OR')}
                      </span>
                      <span className="login__divider-line" />
                    </div>

                    <div className="login__signup">
                      <span className="login__signup-label">
                        {translate(
                          'PAGES.AUTH.LOGIN.FORM.CREATE_ACCOUNT.NEW_ON_BUNI'
                        )}
                      </span>
                      <button
                        type="button"
                        className="login__signup-btn"
                        onClick={() => createAccount()}
                      >
                        {translate('PAGES.AUTH.LOGIN.FORM.CREATE_ACCOUNT.TEXT')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </AdvancedForm>
        </div>
      </div>
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
      <MessageModal
        title={translate('PAGES.AUTH.LOGIN.LOGINS_FAILED_WARNING_MODAL.TITLE')}
        message={translate(
          'PAGES.AUTH.LOGIN.LOGINS_FAILED_WARNING_MODAL.MESSAGE'
        )}
        open={showRecoveryPassowordWarnModal}
        setVisible={() => {
          dispatch({
            payload: false,
            type: WARN_LOGIN_ATTEMPTS_ERROR,
          });
        }}
        buttonCancelText={translate(
          'PAGES.AUTH.LOGIN.LOGINS_FAILED_WARNING_MODAL.BUTTONS.CANCEL'
        )}
        buttonOkText={translate(
          'PAGES.AUTH.LOGIN.LOGINS_FAILED_WARNING_MODAL.BUTTONS.RECOVERY_PASSWORD'
        )}
        onCancel={() => {
          dispatch({
            payload: false,
            type: WARN_LOGIN_ATTEMPTS_ERROR,
          });
        }}
        onConfirm={() => {
          dispatch({
            payload: false,
            type: WARN_LOGIN_ATTEMPTS_ERROR,
          });
          navigate(getRouteStackPath('AUTH', 'RECOVER_PASSWORD'), {
            state: {
              userLogin: login,
            },
          });
        }}
      />
    </div>
  );
};

export default Login;
